import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { API } from '../../shared/api/Api';
import { selectUserInformations } from '../../features/authSlice';
import { 
  fetchRoles, 
  deleteRole, 
  selectRoles, 
  selectRolesListStatus,
  selectRoleDeleteStatus, 
  selectRoleDeleteError 
} from '../../features/roles/roleSlice';
import Loader from '../../components/Live/Loader';
import { hasPermission, showToast } from '../../utils/utils';
import ActionsDropdown from '../../components/reusable/ActionsDropdown';
import { MdDeleteOutline } from 'react-icons/md';
import Modal from '../../components/Modal';
import AddPermission from '../../assets/image/add_Permission.png';
import AssignRole from '../../assets/image/AssignRole.png';
import AssignTempRoleImage from '../../assets/image/AssignTempRole.png';
import AddPermissionToRole from '../../components/Roles/AddPermissionToRole';
import AssignTempRole from '../../components/Roles/AssignTempRole';
import AssignRoleToUser from '../../components/Roles/AssignRoleToUser';
import Bin from '../../assets/svg/Bin.svg';
import RemoveRoleFromUser from '../../components/Roles/RemoveRoleFromUser';

const DEBOUNCE_DELAY = 700;

const RolePage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [actionType, setActionType] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [roleDetails, setRoleDetails] = useState(null);
    
    const user = useSelector(selectUserInformations);
    const roles = useSelector(selectRoles);
    const listStatus = useSelector(selectRolesListStatus);
    const deleteStatus = useSelector(selectRoleDeleteStatus);
    const deleteError = useSelector(selectRoleDeleteError);

    const loader = listStatus === 'loading';

    // Fetch roles using Redux
    useEffect(() => {
        if (user?.token) {
            dispatch(fetchRoles({ 
                token: user.token, 
                params: { search: debouncedSearch } 
            }));
        }
    }, [dispatch, user?.token, debouncedSearch]);

    const fetchRoleDetails = async (roleId) => {
        try {
            const { data } = await axios.get(`${API.Roles.app_roles_get}/${roleId}`, {
                headers: {
                    Authorization: `Bearer ${user?.token}`
                }
            });

            if (data.status === 200) {
                setRoleDetails(data.data);
            } else {
                showToast('Error', data.message || 'Failed to fetch role details', 'error');
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Failed to fetch role details', 'error');
        }
    };

    // Handle delete with confirmation
    const handleDelete = async (roleId) => {
        if (window.confirm("Are you sure you want to delete this role? There is no going back!!")) {
            const result = await dispatch(deleteRole({ token: user.token, roleId }));
            
            if (deleteRole.fulfilled.match(result)) {
                showToast('Success', 'Role deleted successfully', 'success');
                // Refetch roles list
                dispatch(fetchRoles({ 
                    token: user.token, 
                    params: { search: debouncedSearch } 
                }));
            } else if (deleteRole.rejected.match(result)) {
                showToast('Error', result.payload || 'Failed to delete role', 'error');
            }
        }
    };

    // Handle delete errors
    useEffect(() => {
        if (deleteStatus === 'failed' && deleteError) {
            showToast('Error', deleteError, 'error');
        }
    }, [deleteStatus, deleteError]);

    // Handle opening modals for special operations only
    const handleOpenModal = (type, id = null) => {
        setActionType(type);
        setSelectedRoleId(id);
        setIsModalOpen(true);

        if (type === 'add_permission_to_role' || type === 'assign_temp_role' || type === 'assign_role_to_user' || type === 'remove_role_from_user') {
            fetchRoleDetails(id);
        }
    };

    const getDropdownActions = (id) => {
        const actions = [];

        if (hasPermission(user, "view_role")) {
            actions.push({
                icon: (
                    <svg
                        className="w-4 h-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                        />
                    </svg>
                ),
                label: "Preview",
                onClick: () => navigate(`/dashboard/roles/${id}`),
            });
        }

        if (hasPermission(user, "delete_role")) {
            actions.push({
                icon: <MdDeleteOutline className="w-4 h-4" />,
                label: "Delete",
                onClick: () => handleDelete(id),
            });
        }

        return actions;
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setActionType(null);
        setSelectedRoleId(null);
        setRoleDetails(null);
        // Refetch roles list
        if (user?.token) {
            dispatch(fetchRoles({ 
                token: user.token, 
                params: { search: debouncedSearch } 
            }));
        }
    };

    const renderModalContent = () => {
        switch (actionType) {
            case 'add_permission_to_role':
                return <AddPermissionToRole roleDetails={roleDetails} onClose={handleCloseModal} />;
            case 'assign_temp_role':
                return <AssignTempRole role={roleDetails} onClose={handleCloseModal} />;
            case 'assign_role_to_user':
                return <AssignRoleToUser role={roleDetails} onClose={handleCloseModal} />;
            case 'remove_role_from_user':
                return <RemoveRoleFromUser role={roleDetails} onClose={handleCloseModal} />;
            default:
                return null;
        }
    };

    // Table Headers
    const tableHeaders = [
        "Role Name",
        "Created At",
        "Add Permission",
        "Assign Temporary",
        "Assign Role To User",
        "Remove Role From User",
        (
            hasPermission(user, "update_role") ||
            hasPermission(user, "delete_role") ||
            hasPermission(user, "view_role")
        ) && "Actions",
    ];


    // Search effect
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(handler);
    }, [search]);

    return (
        <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
            <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
                            Roles
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage system roles, permissions, and user assignments.
                        </p>
                    </div>

                    {/* Add Role Button */}
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        {hasPermission(user, "create_role") && (
                            <button
                                type="button"
                                onClick={() => navigate("/dashboard/roles/create")}
                                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                                title="Add Role"
                            >
                                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                                    <svg
                                        className="w-3.5 h-3.5"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 18 18"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 1v16M1 9h16"
                                        />
                                    </svg>
                                </span>
                                <span className="hidden sm:inline">New Role</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Header: Search */}
                <div className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-gray-100 pt-4 gap-3">
                    <span className="text-sm sm:text-base font-medium text-gray-800">
                        All roles
                    </span>
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search roles..."
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
                        />
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                                />
                            </svg>
                        </span>
                    </div>
                </div>

                {/* Table Container */}
                <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
                    <div className="w-full overflow-x-auto">
                        <div className="min-w-[960px]">
                            {loader && (
                                <div className="flex justify-center items-center py-6">
                                    <Loader />
                                </div>
                            )}
                            <table className="w-full table-auto">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        {tableHeaders.map(
                                            (header, idx) =>
                                                header && (
                                                    <th
                                                        key={idx}
                                                        className="px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100"
                                                    >
                                                        {header}
                                                    </th>
                                                )
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {roles.length === 0 && !loader ? (
                                        <tr>
                                            <td
                                                colSpan={tableHeaders.filter(Boolean).length}
                                                className="px-4 sm:px-6 py-10 text-center text-gray-500"
                                            >
                                                No roles found
                                            </td>
                                        </tr>
                                    ) : (
                                        roles.map((item, index) => (
                                            <tr
                                                key={index}
                                                className="hover:bg-gray-50 transition-colors cursor-auto text-sm"
                                            >
                                                <td className="px-4 sm:px-6 py-4 text-gray-900 font-medium">
                                                    {item.name}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 text-gray-700">
                                                    {item.created_at}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4">
                                                    <div
                                                        className="flex flex-row space-x-2 cursor-pointer items-center"
                                                        onClick={() =>
                                                            handleOpenModal(
                                                                "add_permission_to_role",
                                                                item.uuid
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            className="w-5 h-5 md:w-6 md:h-6 sl:w-7 sl:h-7 cursor-pointer"
                                                            src={AddPermission}
                                                            alt="add permission"
                                                        />
                                                        <span className="text-[10px] md:text-[13px] text-gray-700">
                                                            Add Permission To Role
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4">
                                                    <div
                                                        className="flex flex-row space-x-2 cursor-pointer items-center"
                                                        onClick={() =>
                                                            handleOpenModal(
                                                                "assign_temp_role",
                                                                item.uuid
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            className="w-5 h-5 md:w-6 md:h-6 sl:w-7 sl:h-7 cursor-pointer"
                                                            src={AssignTempRoleImage}
                                                            alt="assign temp role"
                                                        />
                                                        <span className="text-[10px] md:text-[13px] text-gray-700">
                                                            Assign Temp Role To User
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4">
                                                    <div
                                                        className="flex flex-row space-x-2 cursor-pointer items-center"
                                                        onClick={() =>
                                                            handleOpenModal(
                                                                "assign_role_to_user",
                                                                item.uuid
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            className="w-5 h-5 md:w-6 md:h-6 sl:w-7 sl:h-7 cursor-pointer"
                                                            src={AssignRole}
                                                            alt="assign role"
                                                        />
                                                        <span className="text-[10px] md:text-[13px] text-gray-700">
                                                            Assign Role To User
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4">
                                                    <div
                                                        className="flex flex-row space-x-2 cursor-pointer items-center"
                                                        onClick={() =>
                                                            handleOpenModal(
                                                                "remove_role_from_user",
                                                                item.uuid
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            className="w-5 h-5 md:w-6 md:h-6 sl:w-7 sl:h-7 cursor-pointer"
                                                            src={Bin}
                                                            alt="remove role"
                                                        />
                                                        <span className="text-[10px] md:text-[13px] text-gray-700">
                                                            Remove Role From User
                                                        </span>
                                                    </div>
                                                </td>
                                                {(hasPermission(user, "update_role") ||
                                                    hasPermission(user, "delete_role") ||
                                                    hasPermission(user, "view_role")) && (
                                                    <td className="px-4 sm:px-6 py-4">
                                                        <ActionsDropdown
                                                            actions={getDropdownActions(item.uuid)}
                                                        />
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Modal for special operations only */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    width="max-w-7xl"
                    withHeader={false}
                    withButtons={false}
                >
                    {renderModalContent()}
                </Modal>
            </div>
        </div>
    );
};

export default RolePage;
